import DocumentChatBox from '@/components/DocumentChatBox/DocumentChatBox';
import FileUpload from '@/components/FileUpload/FileUpload';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import PrimaryButton from '@/components/ui/shared/Button/PrimaryButton/PrimaryButton';
import SecondaryButton from '@/components/ui/shared/Button/SecondaryButton/SecondaryButton';
import {
	attachDocumentToSection,
	attachFormativeDocument,
	getDocumentFromSource,
	uploadDocumentInOrganization,
} from '@/lib/functions/apiCalls';
import {
	ErrorHandle,
	handleApiError,
	handleFileRejections,
	handleGeneralError,
	handleUnexpectedError,
} from '@/lib/functions/funcUtils';
import { useAppDispatch, useAppSelector } from '@/lib/hooks/hooks';
import {
	ApiResponse,
	ErrorResponse,
	UploadDocumentPayload,
	UploadDocumentResponse,
} from '@/lib/types/apiTypes';
import { docName } from '@/lib/types/constants';
import { closeDialog, openDialog } from '@/redux/slices/pageSlice';
import { fetchProposalById } from '@/redux/slices/proposalSlice';
import { RootState } from '@/redux/store/store';
import { useState } from 'react';
// import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import styles from './DocumentDialog.module.css';
import { cn } from '@/lib/utils';
import DocumentUploadUpward from '@/components/ui/icons/new/DocumentUploadUpward';
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '@/components/ui/tooltip';
import DocumentNotFound from '@/components/ui/icons/new/DocumentNotFound';
import UnselectDocument from '@/components/ui/icons/new/UnselectDocument';
import { cloneDeep } from 'lodash';
import Lottie from 'lottie-react';
import LoadingLogo from '@/assets/lotties/logo_loading_v1.json';
import { X } from 'lucide-react';
import DialogLoader from '../../Loader/DialogLoader/DailogLoader';
import CategoryDropdown from '@/components/CategoryDropdown/CategoryDropdown';

const DocumentDialog = () => {
	const dispatch = useAppDispatch();
	const isOpen = useAppSelector(
		(state: RootState) => state.page.dialog?.type === 'addDocuments'
	);
	const addDocumentType = useAppSelector(
		(state: RootState) => state.page.dialog?.addDocumentType
	);
	const proposal = useAppSelector((state: RootState) => state.proposal);
	const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
	const [docData, setDocData] = useState<UploadDocumentResponse | null>(null);
	const [docLoading, setDocLoading] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [uploadedDocument, setUploadedDocument] =
		useState<UploadDocumentResponse | null>(null);
	const { nodeId } = useParams();
	const id = proposal.proposal?.id;
	const handleFiles = async (files: File[]) => {
		if (!files.length) return;
		try {
			setIsLoading(true);
			const payload: UploadDocumentPayload = {
				file: files[0],
				name: docName,
				type: 'text',
				category: selectedCategory as string,
			};

			const response: ApiResponse = await uploadDocumentInOrganization(payload);
			if (response.statusCode >= 400 && response.statusCode <= 599) {
				throw response; // Throw the response if status is 400s or 500s
			}
			if (response.statusCode === 201) {
				const docResponse = response.data as UploadDocumentResponse;
				setUploadedDocument(docResponse);
				setDocData(cloneDeep(docResponse));
			}
			setIsLoading(false);
		} catch (error: unknown) {
			if (error instanceof Error) {
				handleGeneralError(error);
			} else if (
				typeof error === 'object' &&
				error !== null &&
				'message' in error
			) {
				const apiError = error as ErrorResponse;
				handleApiError(apiError);
			} else {
				handleUnexpectedError();
			}
			setIsLoading(false);
		}
	};

	const handleAttachmentSuccess = () => {
		dispatch(fetchProposalById(Number(id)));
		clearDocumentDialog();
		if (addDocumentType === 'attachFormativeDocumentDialog') {
			dispatch(openDialog({ type: 'addDocuments' }));
		} else {
			dispatch(closeDialog());
		}
	};

	const handleAttachDocumentToSection = async () => {
		try {
			setIsLoading(true);
			const response = await attachDocumentToSection(
				Number(id),
				Number(nodeId),
				docData?.documentId as number
			);
			if (response.statusCode >= 400 && response.statusCode <= 599) {
				throw response; // Throw the response if status is 400s or 500s
			}
			if (response.statusCode === 201) {
				handleAttachmentSuccess();
			}
			setIsLoading(false);
		} catch (error: unknown) {
			ErrorHandle(dispatch, error);
			setIsLoading(false);
		}
	};

	const handleAttachFormativeDocument = async () => {
		try {
			setIsLoading(true);
			const response = await attachFormativeDocument(
				Number(id),
				docData?.documentId as number
			);
			if (response.statusCode >= 400 && response.statusCode <= 599) {
				throw response; // Throw the response if status is 400s or 500s
			}
			if (response.statusCode === 201) {
				handleAttachmentSuccess();
			}
		} catch (error: unknown) {
			ErrorHandle(dispatch, error);
			setIsLoading(false);
		}
	};

	const handleAttachment = async () => {
		switch (addDocumentType) {
			case 'attachFormativeDocument':
			case 'attachFormativeDocumentDialog':
				handleAttachFormativeDocument();
				break;
			case 'attachSectionDocument':
				handleAttachDocumentToSection();
				break;
		}
	};

	const selectUploadedDocument = () => {
		setDocData(cloneDeep(uploadedDocument));
	};

	const clearDocumentDialog = () => {
		setDocData(null);
		setUploadedDocument(null);
		setSelectedCategory(null);
		setIsLoading(false);
	};

	const removeUploadedDocument = () => {
		if (uploadedDocument?.documentId === docData?.documentId) {
			setDocData(null);
		}
		setSelectedCategory(null);
		setUploadedDocument(null);
	};

	const renderUploadButton = () => {
		return (
			<PrimaryButton disabled={!selectedCategory}>
				Upload Document
			</PrimaryButton>
		);
	};

	const readDocument = async () => {
		try {
			if (docData?.sourceLink) {
				setIsLoading(true);
				const response = await getDocumentFromSource(docData?.sourceLink);
				if (response) {
					clearDocumentDialog();
					// setPdfData(response as ArrayBuffer);
					dispatch(
						openDialog({ type: 'viewPDF', pdf: response as ArrayBuffer })
					);
					setIsLoading(false);
				}
			}
		} catch (error: unknown) {
			ErrorHandle(dispatch, error);
			setIsLoading(false);
		}
	};

	return (
		<Dialog open={isOpen}>
			<DialogContent
				hideCloseBtn={addDocumentType === 'attachFormativeDocumentDialog'}
				className="flex flex-col h-[75%] overflow-hidden max-w-[90%] 2xl:max-w-[1200px] bg-white p-6"
				setModalOpen={() => clearDocumentDialog()}
			>
				<div className="flex justify-between">
					<h3 className="font-bold text-black dynamic-large">
						Select a{' '}
						{addDocumentType === 'attachFormativeDocument' ||
							(addDocumentType === 'attachFormativeDocumentDialog' &&
								' Formative ')}{' '}
						Document
					</h3>
					{addDocumentType === 'attachFormativeDocumentDialog' && (
						<X
							onClick={() => {
								clearDocumentDialog();
								dispatch(openDialog({ type: 'addDocuments' }));
							}}
							className="w-10 h-10 p-2 bg-white rounded-full cursor-pointer stroke-primary"
						/>
					)}
				</div>
				<div className="flex flex-col h-full min-h-0">
					<div className="flex h-full gap-3 xl:gap-4">
						<div className="flex flex-col w-3/5">
							<div className="flex justify-between gap-3 p-3 bg-white shadow-main rounded-xl ">
								{uploadedDocument?.metaData?.title ? (
									<div className="inline-flex p-4 rounded-xl bg-[#EAF1FC] justify-between gap-6 w-full">
										<p
											className="text-sm cursor-pointer text-primary line-clamp-1"
											onClick={selectUploadedDocument}
										>
											{uploadedDocument?.metaData?.title}
										</p>
										<UnselectDocument
											onClick={removeUploadedDocument}
											className="w-6 h-6 cursor-pointer"
										/>
									</div>
								) : (
									<div className="flex items-center justify-between flex-1">
										<div className="flex items-center justify-start gap-2 ">
											<h6 className="flex items-center gap-1 font-bold text-black shrink-0">
												<DocumentUploadUpward className="w-6 h-6" /> Upload
												Document
											</h6>
										</div>
										<div className="flex items-center justify-end gap-2">
											<div className="flex  w-full max-w-[250px] shrink-0">
												<CategoryDropdown
													className="h-11 border border-[#5D9BFD]"
													setSelectedCategory={setSelectedCategory}
												/>
											</div>
											<div>
												<TooltipProvider delayDuration={50}>
													<Tooltip>
														<TooltipTrigger>
															{selectedCategory ? (
																<FileUpload
																	type="custom"
																	onDropAccepted={handleFiles}
																	onDropRejected={(fileRejections) =>
																		handleFileRejections({
																			fileRejections: fileRejections,
																		})
																	}
																	acceptedExtensions={['.pdf', '.docx']}
																	customLayout={renderUploadButton()}
																/>
															) : (
																renderUploadButton()
															)}
														</TooltipTrigger>
														<TooltipContent
															hidden={!!selectedCategory}
															className="bg-white text-[#6D7D86] text-xs 2xl:text-sm px-3 2xl:px-5 2xl:py-1 rounded-full border-none shadow-none drop-shadow-effect"
														>
															<p>Please select category first</p>
														</TooltipContent>
													</Tooltip>
												</TooltipProvider>
											</div>
										</div>
									</div>
								)}
							</div>
							<div className={cn(styles.separator, 'flex flex-col gap-4 my-6')}>
								<p
									className={cn(
										styles.separator_text,
										'text-base font-bold text-black px-4'
									)}
								>
									OR
								</p>
							</div>
							<DocumentChatBox
								documentType={
									addDocumentType === 'attachSectionDocument'
										? 'section'
										: 'formative'
								}
								setDocLoading={setDocLoading}
								setDocData={setDocData}
							/>
						</div>
						<div className="flex flex-col justify-between w-2/5 gap-3 p-4 rounded-xl shadow-main">
							<div className="flex border-b border-b-[#C6DBFB] pb-4">
								<h4 className="font-bold text-black capitalize text-heading6 xl:text-heading5">
									Selected Document
								</h4>
							</div>
							{docLoading ? (
								<div className="flex flex-col items-center justify-center h-full gap-4">
									<Lottie
										className="block animation-lottie max-h-24 max-w-24"
										animationData={LoadingLogo}
										loop={true}
										autoPlay={true}
									/>
								</div>
							) : docData ? (
								<div className="flex flex-col h-full gap-3 overflow-hidden xl:gap-4">
									<div className="pb-3 xl:pb-4 border-b border-b-[#C6DBFB]">
										<div className="flex items-start gap-3 2xl:gap-4 ">
											{/* <DocumentIcon className="w-20 h-10 pt-2" /> */}
											<p className="text-sm font-bold text-black capitalize">
												{docData?.metaData?.title}
											</p>
										</div>
										<p className="mt-2 text-sm text-black">
											<span className="font-bold text-black">Category:</span>{' '}
											{docData?.metaData?.category}
										</p>
									</div>
									<div className="flex flex-col h-full overflow-hidden">
										<p className="text-sm font-bold text-black">Summary</p>
										<p className="py-1 pr-2 overflow-y-auto text-base text-black dynamic-small">
											{docData?.metaData?.summary}
										</p>
									</div>
								</div>
							) : (
								<div className="flex flex-col items-center justify-center h-full gap-4">
									<DocumentNotFound />
									<div className="flex flex-col items-center gap-2">
										<h6 className="font-bold text-black text-heading6">
											No Document selected
										</h6>
										<p className="text-black dynamic-text">
											Please select a document to begin.
										</p>
									</div>
								</div>
							)}
							<div className="flex justify-center gap-3 xl:gap-4">
								<SecondaryButton onClick={readDocument} disabled={!docData}>
									Read
								</SecondaryButton>
								<PrimaryButton onClick={handleAttachment} disabled={!docData}>
									Select document
								</PrimaryButton>
							</div>
						</div>
					</div>
				</div>
				<DialogLoader isLoading={isLoading} />
			</DialogContent>
		</Dialog>
	);
};

export default DocumentDialog;
